import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { fmtJMD, formatApiError } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

const BLANK = {
  name: "", description: "", price: 650, image_url: "", category: "flavour",
  stock: 0, low_stock_threshold: 10, available_for_build_a_box: true, active: true, tags: [],
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/products?active_only=false");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing, price: Number(editing.price), stock: Number(editing.stock),
                        low_stock_threshold: Number(editing.low_stock_threshold) };
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Catalogue</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Products & flavours</h1>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary inline-flex items-center gap-2" data-testid="new-product-btn">
          <Plus size={16} /> New product
        </button>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-zinc-50">
            <tr className="text-zinc-500 text-xs">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Price</th>
              <th className="text-left px-4 py-3">Stock</th>
              <th className="text-left px-4 py-3">Build Yuh Own Box</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100" data-testid={`product-row-${p.id}`}>
                <td className="px-4 py-3 flex items-center gap-3">
                  {p.image_url && <img src={p.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url} alt="" className="w-8 h-8 rounded-md object-cover" />}
                  <span className="font-semibold">{p.name}</span>
                </td>
                <td className="px-4 py-3">{fmtJMD(p.price)}</td>
                <td className="px-4 py-3">
                  <span className={p.stock <= p.low_stock_threshold ? "text-amber-600 font-semibold" : ""}>{p.stock}</span>
                </td>
                <td className="px-4 py-3">{p.available_for_build_a_box ? "Yes" : "No"}</td>
                <td className="px-4 py-3">{p.active ? <span className="text-green-600">Active</span> : <span className="text-zinc-400">Inactive</span>}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...p })} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-product-${p.id}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit product" : "New product"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name"><input className="input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-name" /></Field>
            <Field label="Price (JMD)"><input type="number" className="input" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="edit-price" /></Field>
            <Field label="Product image" span={2}>
              <ImageUpload value={editing.image_url || ""} onChange={(url) => setEditing({ ...editing, image_url: url })} testid="edit-image-upload" />
            </Field>
            <Field label="Description" span={2}><textarea className="input" rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="edit-description" /></Field>
            <Field label="Stock"><input type="number" className="input" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="edit-stock" /></Field>
            <Field label="Low-stock threshold"><input type="number" className="input" value={editing.low_stock_threshold} onChange={(e) => setEditing({ ...editing, low_stock_threshold: e.target.value })} data-testid="edit-threshold" /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.available_for_build_a_box} onChange={(e) => setEditing({ ...editing, available_for_build_a_box: e.target.checked })} /> Available in Build Yuh Own Box</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-product-btn">Save</button>
          </div>
        </Modal>
      )}
      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; }`}</style>
    </div>
  );
}

function Field({ label, span = 1, children }) {
  return <div className={span === 2 ? "sm:col-span-2" : ""}>
    <label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>{children}
  </div>;
}
function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 sm:p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-lg sm:text-xl">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
